import { useEffect, useRef, useState } from 'react';
import Button from './Button';
import './EpubUploadModal.css';

const normalizeTitle = (value) => value.trim().toLowerCase();
const normalizeAuthor = (value) => value.trim().toLowerCase();
const CLOSE_DURATION_MS = 180;

function EpubUploadModal({ onClose, onUploadSuccess }) {
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [summary, setSummary] = useState('');
  const [contentType, setContentType] = useState('');
  const [fandomDetails, setFandomDetails] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPartOfSeries, setIsPartOfSeries] = useState(false);
  const [seriesTitle, setSeriesTitle] = useState('');
  const [seriesOrder, setSeriesOrder] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const fileInputRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const isSubmitDisabled = !title.trim() || !contentType || !selectedFile || loading;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const normalizedName = String(file.name || '').trim().toLowerCase();
    const normalizedType = String(file.type || '').trim().toLowerCase();
    const looksLikeEpub = normalizedName.endsWith('.epub')
      || normalizedType === 'application/epub+zip'
      || normalizedType === 'application/octet-stream';

    if (!looksLikeEpub) {
      setSelectedFile(null);
      setError('Please choose an EPUB file.');
      e.target.value = '';
      return;
    }

    setError('');
    setSelectedFile(file);
  };

  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyPosition = document.body.style.position;
    const previousBodyTop = document.body.style.top;
    const previousBodyWidth = document.body.style.width;
    const previousBodyLeft = document.body.style.left;
    const previousBodyRight = document.body.style.right;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.width = previousBodyWidth;
      document.body.style.left = previousBodyLeft;
      document.body.style.right = previousBodyRight;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const requestClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    closeTimeoutRef.current = window.setTimeout(() => {
      onClose?.();
    }, CLOSE_DURATION_MS);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Read the values displayed by the browser at the instant of submission.
    // Mobile keyboards can update the DOM before React's controlled state has
    // completed its final onChange, so building the payload from state can send
    // an empty title even though the field visibly contains one.
    const formData = new FormData(e.currentTarget);
    const submittedTitle = String(formData.get('title') ?? title);
    const submittedAuthor = String(formData.get('author') ?? author);
    const submittedSummary = String(formData.get('summary') ?? summary);
    const submittedContentType = String(formData.get('contentType') ?? contentType);
    const submittedFandomDetails = String(formData.get('fandomDetails') ?? fandomDetails);
    const submittedSeriesTitle = String(formData.get('seriesTitle') ?? seriesTitle);
    const submittedSeriesOrder = String(formData.get('seriesOrder') ?? seriesOrder);
    const trimmedTitle = submittedTitle.trim();
    const apiUrl = process.env.REACT_APP_API_URL;

    if (!trimmedTitle || !submittedContentType || !selectedFile) {
      setError('Please provide a title, type, and EPUB file.');
      setLoading(false);
      return;
    }

    try {
      const existingBooksResponse = await fetch(`${apiUrl}/get-epubs.php`, {
        credentials: 'include',
      });

      if (existingBooksResponse.ok) {
        const existingBooksPayload = await existingBooksResponse.json();
        const existingBooks = Array.isArray(existingBooksPayload)
          ? existingBooksPayload
          : existingBooksPayload?.epubs;
        const normalizedTitle = normalizeTitle(trimmedTitle);
        const normalizedAuthor = normalizeAuthor(submittedAuthor);
        const hasDuplicateTitle = Array.isArray(existingBooks)
          && existingBooks.some((book) => (
            normalizeTitle(book?.title || '') === normalizedTitle
            && normalizeAuthor(book?.author || '') === normalizedAuthor
          ));

        if (hasDuplicateTitle) {
          setError('A book with this title and author already exists.');
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('Duplicate title check failed:', err);
    }

    formData.set('title', trimmedTitle);
    formData.set('author', submittedAuthor);
    formData.set('summary', submittedSummary);
    formData.set('contentType', submittedContentType);
    formData.set('fandomDetails', submittedFandomDetails);
    formData.set('seriesTitle', submittedSeriesTitle);
    formData.set('seriesOrder', submittedSeriesOrder);
    formData.set('file', selectedFile, selectedFile.name);

    try {
      const response = await fetch(`${apiUrl}/upload-epub.php`, {
        method: 'POST',
        headers: {
          'X-Remember-Token': localStorage.getItem('rememberToken') || ''
        },
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        await onUploadSuccess();
        requestClose();
      } else {
        setError(data.error || 'Upload failed. Please try again.');
      }
    } catch (err) {
      setError('Could not connect to server. Please check your connection.');
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`modal-overlay${isClosing ? ' modal-overlay--closing' : ''}`} onClick={requestClose}>
      <div
        className={`modal-container container-fluid bg-white rounded p-4${isClosing ? ' modal-container--closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex justify-content-end">
          <button type="button" className="modal-close-btn" onClick={requestClose}>
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <h4 className="serif-text modal-title-heading text-center">Upload A File</h4>
        <p className="modal-subtitle text-center mb-4">Viewable to Everyone</p>

        <form onSubmit={handleSubmit} className="d-flex flex-column">
          <div className="modal-form-inputs w-100 d-flex flex-column align-items-start gap-2 py-3">
            <Button as="input" type="text" name="title" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Button as="input" type="text" name="author" placeholder="Author" value={author} onChange={(e) => setAuthor(e.target.value)} />
            <textarea
              name="summary"
              className="btn-neutral modal-textarea"
              placeholder="Summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
            />

            <div className="modal-select-wrapper">
              <select
                name="contentType"
                className="btn-neutral modal-select w-100"
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                style={{ color: contentType ? '#430F10' : 'rgba(67,15,16,0.4)' }}
              >
                <option value="" disabled>Type</option>
                <option value="Fiction">Fic</option>
                <option value="Book">Book</option>
              </select>
              <span className="material-symbols-rounded modal-select-arrow">keyboard_arrow_down</span>
            </div>

            {contentType === 'Fiction' && (
              <Button
                as="input"
                type="text"
                name="fandomDetails"
                placeholder="Specify fandom / ship / dynamic (if applicable)"
                value={fandomDetails}
                onChange={(e) => setFandomDetails(e.target.value)}
              />
            )}

            <div className="modal-select-wrapper">
              <select
                className="btn-neutral modal-select w-100"
                value={isPartOfSeries ? 'yes' : 'no'}
                onChange={(e) => { const yes = e.target.value === 'yes'; setIsPartOfSeries(yes); if (!yes) { setSeriesTitle(''); setSeriesOrder(''); } }}
              >
                <option value="no">Not part of a series</option>
                <option value="yes">Part of a series</option>
              </select>
              <span className="material-symbols-rounded modal-select-arrow">keyboard_arrow_down</span>
            </div>

            {isPartOfSeries && (
              <>
                <Button as="input" type="text" name="seriesTitle" placeholder="Series Title" value={seriesTitle} onChange={(e) => setSeriesTitle(e.target.value)} />
                <div className="series-counter-wrapper">
                  <button type="button" className="series-counter-btn series-counter-btn--left" onClick={() => setSeriesOrder(String(Math.max(1, (parseInt(seriesOrder) || 1) - 1)))}>−</button>
                  <input
                    type="number"
                    name="seriesOrder"
                    className="btn-neutral series-counter-input"
                    placeholder="Book Number"
                    value={seriesOrder}
                    onChange={(e) => setSeriesOrder(e.target.value)}
                  />
                  <button type="button" className="series-counter-btn series-counter-btn--right" onClick={() => setSeriesOrder(String((parseInt(seriesOrder) || 0) + 1))}>+</button>
                </div>
              </>
            )}

            <div className="file-upload-section d-flex flex-column align-items-center gap-2 p-3 btn-neutral w-50" onClick={() => fileInputRef.current.click()}>
              <span className="material-symbols-rounded upload-icon">file_save</span>
              <p className="file-label text-center mb-0">
                {selectedFile ? selectedFile.name : 'No file selected'}
              </p>
              <input
                type="file"
                name="file"
                accept=".epub,.EPUB,application/epub+zip,application/octet-stream"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="modal-file-input"
              />
            </div>
          </div>

          {error && <div className="modal-error-box">{error}</div>}

          <div className="upload-btn-section mt-4 mb-4">
            <Button variant="cta" type="submit" disabled={isSubmitDisabled}>
              <div className="d-flex align-items-center justify-content-center gap-2">
                {loading ? 'Uploading...' : 'Upload Book'}
                <span className="material-symbols-rounded modal-upload-icon">book_ribbon</span>
              </div>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EpubUploadModal;
